import {
    captureException,
    store,
    userSetThriveTosAcceptedServerTime
} from '@thriveglobal/thrive-web-core'
import {
    CoreTypography,
    LoadingButton,
    type LoadingButtonProps
} from '@thriveglobal/thrive-web-leafkit'
import React, {
    BaseSyntheticEvent,
    memo,
    PropsWithChildren,
    useCallback,
    useState
} from 'react'
import { FormattedMessage } from 'react-intl'
import {
    AcceptCompanyToSInput,
    useAcceptCompanyToSMutation,
    useSetUserTncAcceptanceTimestampMutation
} from '../../../../../graphql/generated/autogenerated'
import { PolicyChecks } from '../ToSAcceptance/ToSAcceptanceFormController'

export type SaveToSAcceptanceProps = Omit<
    LoadingButtonProps,
    'fixWidth' | 'onClick' | 'loading'
> & {
    saveLoading?: boolean
    onSaveToSAcceptance?: () => Promise<void>
    onSavedToSAcceptance?: (success: boolean) => Promise<void>
    onHandleFormSubmit?: (
        acceptTos: (formValues?: PolicyChecks) => Promise<void>
    ) => (e?: BaseSyntheticEvent<object, any, any> | undefined) => Promise<void>
}

export const SaveToSAcceptance: React.FC<
    PropsWithChildren<SaveToSAcceptanceProps>
> = ({
    saveLoading,
    onSaveToSAcceptance: onSaveToSAcceptanceExternal = () => Promise.resolve(),
    onSavedToSAcceptance,
    onHandleFormSubmit,
    children,
    ...props
}) => {
    const [loading, setLoading] = useState(false)
    const [setUserTncAcceptanceTimestamp] =
        useSetUserTncAcceptanceTimestampMutation()
    const [acceptCompanyToS] = useAcceptCompanyToSMutation()

    const saveToSAcceptance = useCallback(
        async (formValues?: PolicyChecks) => {
            try {
                // Create inputs for each additional company policy, excluding thrivePolicy
                const companyToSInputs: AcceptCompanyToSInput[] =
                    Object.entries(formValues?.policyChecks || {})
                        .filter(([key]) => key !== 'thrivePolicy')
                        .map(([key, policyStatus]) => ({
                            accepted: policyStatus.accepted,
                            name: key,
                            read: policyStatus.read,
                            url: policyStatus.url
                        }))

                // Fire all mutations simultaneously
                return Promise.all([
                    setUserTncAcceptanceTimestamp(),
                    ...(companyToSInputs.length > 0
                        ? [
                              acceptCompanyToS({
                                  variables: { input: companyToSInputs }
                              })
                          ]
                        : [])
                ]).then(async (results) => {
                    const [userTosResult] = results

                    // Check if the main user ToS acceptance was successful
                    if (
                        userTosResult?.data?.identity?.user?.acceptToS
                            ?.serverTime
                    ) {
                        store.dispatch(
                            userSetThriveTosAcceptedServerTime(
                                userTosResult.data?.identity.user.acceptToS
                                    .serverTime
                            )
                        )
                        await onSavedToSAcceptance?.(true)
                    } else {
                        await onSavedToSAcceptance?.(false)
                    }
                })
            } catch (error) {
                captureException(error, {
                    message: 'failed to save ToS acceptance'
                })

                await onSavedToSAcceptance?.(false)
            }
        },
        [acceptCompanyToS, onSavedToSAcceptance, setUserTncAcceptanceTimestamp]
    )

    const onSaveToSAcceptance = useCallback(
        async (formValues?: any) => {
            setLoading(true)
            try {
                await onSaveToSAcceptanceExternal?.()
                await saveToSAcceptance(formValues)
            } finally {
                setLoading(false)
            }
        },
        [onSaveToSAcceptanceExternal, saveToSAcceptance]
    )

    return (
        <LoadingButton
            color="primary"
            fixWidth={true}
            loading={loading || saveLoading}
            size="large"
            variant="contained"
            {...props}
            disabled={loading || saveLoading || props.disabled}
            onClick={
                onHandleFormSubmit
                    ? () => onHandleFormSubmit(onSaveToSAcceptance)()
                    : onSaveToSAcceptance
            }
            data-testid="save-tos-button"
        >
            {children ?? (
                <CoreTypography customVariant="buttonLarge">
                    <FormattedMessage
                        defaultMessage="Save"
                        description="Accept the privacy policy and terms of service"
                    />
                </CoreTypography>
            )}
        </LoadingButton>
    )
}

export default memo(SaveToSAcceptance)
